import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { MorphingOrb } from "@/components/PremiumAnimations";
import { servicesData } from "@/data/services";
import SEOHead from "@/components/SEOHead";

const techStack = [
  { category: "Backend", items: ["Node.js", "Python", "Go", "Rust"] },
  { category: "Frontend", items: ["React", "Next.js", "Vue", "Svelte"] },
  { category: "Mobile", items: ["React Native", "Flutter", "Swift", "Kotlin"] },
  { category: "AI/ML", items: ["OpenAI", "Anthropic Claude", "LangChain", "TensorFlow"] },
  { category: "Databases", items: ["PostgreSQL", "MongoDB", "Redis"] },
  { category: "Blockchain", items: ["Ethereum", "Solana", "Polygon"] },
  { category: "Cloud", items: ["AWS", "GCP", "Vercel", "Supabase"] },
  { category: "DevOps", items: ["Docker", "Kubernetes", "GitHub Actions"] },
];

export default function ServicesPage() {
  return (
    <main data-testid="services-page">
      <SEOHead
        title="Services | Full-Stack Product Development — Venury"
        description="From MVPs to enterprise systems. Full-stack development, AI/ML integration, FinTech infrastructure, trading platforms, Web3, cloud & DevOps, and more."
        url="https://venury.tech/services"
      />
      {/* ==================== HERO ==================== */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <MorphingOrb
          color1="rgba(230, 126, 34, 0.10)"
          color2="rgba(211, 84, 0, 0.06)"
          size={400}
          duration={25}
          style={{ top: "10%", right: "5%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1116]/40 to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="tag-box font-mono text-sm text-orange-bright uppercase tracking-[0.2em] mb-6"
          >
            Services
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0, 0, 0.2, 1] }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tight max-w-4xl leading-[1.05]"
          >
            Comprehensive Product{" "}
            <span className="gradient-text-orange">Development</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl mt-8 mb-12"
          >
            From MVPs to enterprise systems. AI-native engineering across the full stack.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {[
              { value: "8+", label: "Service Lines" },
              { value: "50+", label: "Engineers" },
              { value: "10+", label: "Industries" },
              { value: "99.9%", label: "Client Retention" },
            ].map((stat, i) => (
              <div key={i} className="text-center md:text-left">
                <span className="font-mono text-2xl md:text-3xl font-bold text-teal-bright block">{stat.value}</span>
                <span className="font-mono text-xs text-slate-600 uppercase tracking-wider">{stat.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ==================== SERVICES GRID ==================== */}
      <section data-testid="services-grid-section" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-6" staggerDelay={0.08}>
            {servicesData.map((service, i) => {
              const Icon = service.icon;
              return (
                <StaggerItem key={service.slug}>
                  <Link
                    to={`/services/${service.slug}`}
                    data-testid={`service-detail-${i}`}
                    className="block glass-card rounded-lg p-8 h-full group hover:border-teal-primary/30 transition-colors duration-300"
                  >
                    <div className="flex items-start gap-5 mb-5">
                      <div className="w-12 h-12 rounded-lg bg-teal-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-primary/20 transition-colors duration-300">
                        <Icon size={22} className="text-teal-bright" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-xl font-bold text-white mb-2 group-hover:text-teal-bright transition-colors duration-200">{service.title}</h3>
                        <p className="text-sm text-slate-400 leading-relaxed">{service.shortDesc}</p>
                      </div>
                    </div>

                    <div className="ml-0 md:ml-[68px]">
                      <h4 className="font-mono text-xs text-slate-600 uppercase tracking-widest mb-3">Key Features</h4>
                      <ul className="grid grid-cols-2 gap-2 mb-5">
                        {service.features.slice(0, 4).map((f, j) => (
                          <li key={j} className="text-sm text-slate-400 flex items-start gap-2">
                            <Check size={12} className="text-teal-bright mt-1 flex-shrink-0" />
                            {f.title}
                          </li>
                        ))}
                      </ul>
                      <span className="inline-flex items-center gap-2 font-mono text-xs text-teal-bright uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Learn more <ArrowUpRight size={12} />
                      </span>
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* ==================== TECH STACK ==================== */}
      <section data-testid="tech-stack-section" className="py-20 md:py-32 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">Stack</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-16">
              Built with Best-in-Class Technology
            </h2>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-6" staggerDelay={0.08}>
            {techStack.map((cat, i) => (
              <StaggerItem key={i}>
                <div data-testid={`tech-category-${i}`} className="glass-card rounded-lg p-6 h-full">
                  <h4 className="font-mono text-xs text-teal-bright uppercase tracking-widest mb-4">{cat.category}</h4>
                  <div className="space-y-2">
                    {cat.items.map((item, j) => (
                      <span key={j} className="block font-mono text-sm text-slate-400 hover:text-white transition-colors duration-200">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>
    </main>
  );
}
